import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import { PO_STATUSES } from "../../../constants/inventory";
import { IoArrowBack } from "react-icons/io5";
import { Skeleton } from "@mui/material";

const ViewPurchaseOrder = () => {
  const { token, user } = useAuthStore();
  const navigate = useNavigate();
  const { id } = useParams();

  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const isOwner = user?.role === "Owner";

  useEffect(() => {
    fetchPurchaseOrder();
  }, [id]);

  const fetchPurchaseOrder = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/getPurchaseOrder/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setPurchaseOrder(data.purchaseOrder);
      } else {
        setErrorMessage(data.error || "Failed to fetch purchase order");
      }
    } catch {
      setErrorMessage("Failed to fetch purchase order");
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action, body = {}) => {
    setActionLoading(true);
    try {
      const response = await fetch(`/api/purchaseOrder/${action}/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (response.ok) {
        const actionLabels = {
          submit: "submitted for approval",
          approve: "approved",
          reject: "rejected",
          order: "marked as ordered",
        };
        setSuccessMessage(`Purchase order ${actionLabels[action] || action + "d"} successfully`);
        setPurchaseOrder(data.purchaseOrder);
        setShowRejectModal(false);
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setErrorMessage(data.error || `Failed to ${action} purchase order`);
        setTimeout(() => setErrorMessage(""), 3000);
      }
    } catch {
      setErrorMessage(`Failed to ${action} purchase order`);
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = PO_STATUSES[status] || { label: status, color: "bg-gray-100 text-gray-800" };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="m-2 md:m-6 p-2 md:p-6">
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </div>
    );
  }

  if (!purchaseOrder) {
    return (
      <div className="m-2 md:m-6 p-2 md:p-6 text-center">
        <p className="text-gray-500">Purchase order not found</p>
      </div>
    );
  }

  const status = purchaseOrder.status;

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/PurchaseOrders")}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <IoArrowBack className="text-xl text-primary" />
          </button>
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              {purchaseOrder.poNumber}
            </h2>
            <p className="text-sm text-gray-500">
              Created {new Date(purchaseOrder.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>
        {getStatusBadge(status)}
      </div>

      {/* Messages */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
          {successMessage}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* PO & Vendor Info */}
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Purchase Order Details</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Vendor</p>
                <p className="font-medium">{purchaseOrder.vendor?.name || "N/A"}</p>
              </div>
              <div>
                <p className="text-gray-500">Contact</p>
                <p className="font-medium">{purchaseOrder.vendor?.contactPerson || purchaseOrder.vendor?.email || "N/A"}</p>
              </div>
              <div>
                <p className="text-gray-500">Expected Delivery</p>
                <p className="font-medium">
                  {purchaseOrder.expectedDeliveryDate
                    ? new Date(purchaseOrder.expectedDeliveryDate).toLocaleDateString()
                    : "N/A"}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Created By</p>
                <p className="font-medium">{purchaseOrder.createdBy?.name || "N/A"}</p>
              </div>
            </div>
          </div>

          {/* Vendor Details */}
          {purchaseOrder.vendor && (
            <div className="bg-white rounded-lg shadow p-5">
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Vendor Information</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Company</p>
                  <p className="font-medium">{purchaseOrder.vendor.name || "N/A"}</p>
                </div>
                <div>
                  <p className="text-gray-500">Email</p>
                  <p className="font-medium">{purchaseOrder.vendor.email || "N/A"}</p>
                </div>
                <div>
                  <p className="text-gray-500">Phone</p>
                  <p className="font-medium">{purchaseOrder.vendor.phone || "N/A"}</p>
                </div>
                {purchaseOrder.vendor.address && (
                  <div className="col-span-2">
                    <p className="text-gray-500">Address</p>
                    <p className="font-medium">{purchaseOrder.vendor.address}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Line Items Table */}
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Line Items</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">#</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Item</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Qty</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Received</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  {purchaseOrder.items?.map((item, index) => (
                    <tr key={index} className="border-t">
                      <td className="px-4 py-3 text-sm">{index + 1}</td>
                      <td className="px-4 py-3 text-sm">
                        <div>
                          <p className="font-medium">{item.item?.name || "N/A"}</p>
                          <p className="text-xs text-gray-400">{item.item?.sku || ""}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">{item.qty}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={item.receivedQty >= item.qty ? "text-green-600 font-medium" : ""}>
                          {item.receivedQty || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">Nu. {(item.unitPrice || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm font-medium">
                        Nu. {((item.unitPrice || 0) * (item.qty || 0)).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td colSpan={5} className="px-4 py-2 text-sm text-right text-gray-600">Subtotal:</td>
                    <td className="px-4 py-2 text-sm font-medium">
                      Nu. {(purchaseOrder.subTotal || purchaseOrder.items?.reduce((s, i) => s + (i.unitPrice || 0) * (i.qty || 0), 0) || 0).toLocaleString()}
                    </td>
                  </tr>
                  {(purchaseOrder.taxRate > 0 || purchaseOrder.taxAmount > 0) && (
                    <tr>
                      <td colSpan={5} className="px-4 py-2 text-sm text-right text-gray-600">
                        Tax ({purchaseOrder.taxRate || 0}%):
                      </td>
                      <td className="px-4 py-2 text-sm font-medium">
                        Nu. {(purchaseOrder.taxAmount || 0).toLocaleString()}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td colSpan={5} className="px-4 py-3 text-sm font-semibold text-right">Total:</td>
                    <td className="px-4 py-3 text-sm font-semibold text-primary">
                      Nu. {(purchaseOrder.totalAmount || 0).toLocaleString()}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Notes */}
          {purchaseOrder.notes && (
            <div className="bg-white rounded-lg shadow p-5">
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Notes</h3>
              <p className="text-sm text-gray-700">{purchaseOrder.notes}</p>
            </div>
          )}

          {/* Rejection reason */}
          {purchaseOrder.rejectionReason && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-5">
              <h3 className="text-sm font-semibold text-red-700 uppercase mb-2">Rejection Reason</h3>
              <p className="text-sm text-red-600">{purchaseOrder.rejectionReason}</p>
            </div>
          )}
        </div>

        {/* Right: Actions */}
        <div className="space-y-6">
          {/* Draft actions */}
          {status === "draft" && (
            <div className="bg-white rounded-lg shadow p-5">
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Actions</h3>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => handleAction("submit")}
                  disabled={actionLoading}
                  className="w-full px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
                >
                  {actionLoading ? "Submitting..." : "Submit for Approval"}
                </button>
                <button
                  onClick={() => navigate(`/PurchaseOrders/Edit/${id}`, { state: { purchaseOrder } })}
                  className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
                >
                  Edit
                </button>
              </div>
            </div>
          )}

          {/* Pending approval actions (Owner only) */}
          {isOwner && status === "pending_approval" && (
            <div className="bg-white rounded-lg shadow p-5">
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Approval</h3>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => handleAction("approve")}
                  disabled={actionLoading}
                  className="w-full px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 disabled:opacity-50"
                >
                  {actionLoading ? "Processing..." : "Approve"}
                </button>
                <button
                  onClick={() => setShowRejectModal(true)}
                  disabled={actionLoading}
                  className="w-full px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 disabled:opacity-50"
                >
                  Reject
                </button>
              </div>
            </div>
          )}

          {/* Approved actions */}
          {status === "approved" && (
            <div className="bg-white rounded-lg shadow p-5">
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Actions</h3>
              <button
                onClick={() => handleAction("order")}
                disabled={actionLoading}
                className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50"
              >
                {actionLoading ? "Processing..." : "Mark as Ordered"}
              </button>
            </div>
          )}

          {/* Ordered / Partially Received actions */}
          {(status === "ordered" || status === "partially_received") && (
            <div className="bg-white rounded-lg shadow p-5">
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Goods Receipt</h3>
              <button
                onClick={() => navigate(`/GRN/New?poId=${id}`)}
                className="w-full px-4 py-2 bg-indigo-500 text-white rounded-lg text-sm font-medium hover:bg-indigo-600"
              >
                Receive Goods
              </button>
            </div>
          )}

          {/* Summary card */}
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Summary</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Status</span>
                {getStatusBadge(status)}
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Items</span>
                <span className="font-medium">{purchaseOrder.items?.length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Total Amount</span>
                <span className="font-medium text-primary">Nu. {(purchaseOrder.totalAmount || 0).toLocaleString()}</span>
              </div>
              {purchaseOrder.expectedDeliveryDate && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Expected Delivery</span>
                  <span className="font-medium">
                    {new Date(purchaseOrder.expectedDeliveryDate).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Reject Purchase Order</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4"
              placeholder="Reason for rejection (required)"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowRejectModal(false); setRejectReason(""); }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!rejectReason.trim()) return;
                  handleAction("reject", { rejectionReason: rejectReason });
                }}
                disabled={!rejectReason.trim() || actionLoading}
                className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm disabled:opacity-50"
              >
                {actionLoading ? "Rejecting..." : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewPurchaseOrder;
